import React, { useEffect, useState } from 'react';
import { databaseService } from '../../services/database.service';
import { i18nService } from '../../services/i18n.service';
import { storageService } from '../../services/storage.service';
import AdminLayout from '../../components/layout/AdminLayout';
import Card from '../../components/common/Card';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import LanguageTabs from '../../components/common/LanguageTabs';
import toast from 'react-hot-toast';

const AboutManager = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentLang, setCurrentLang] = useState('nl');
  const [aboutData, setAboutData] = useState({
    companyStory: { nl: '', fr: '', de: '', en: '' },
    mission: { nl: '', fr: '', de: '', en: '' },
    vision: { nl: '', fr: '', de: '', en: '' }
  });
  const [teamMembers, setTeamMembers] = useState([]);
  const [newMember, setNewMember] = useState({
    name: '',
    role: '',
    bio: '',
    photo: null,
    order: 0
  });
  const [editingMember, setEditingMember] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    fetchAboutData();
  }, []);

  const fetchAboutData = async () => {
    try {
      const data = await databaseService.getAboutPage();
      if (data) {
        setAboutData({
          companyStory: i18nService.extractMultiLangField(data, 'companyStory'),
          mission: i18nService.extractMultiLangField(data, 'mission'),
          vision: i18nService.extractMultiLangField(data, 'vision')
        });

        // Convert team members object to array
        if (data.teamMembers) {
          const membersArray = Object.entries(data.teamMembers).map(([id, member]) => ({
            id,
            ...member
          }));
          setTeamMembers(membersArray.sort((a, b) => (a.order || 0) - (b.order || 0)));
        }
      }
    } catch (error) {
      console.error('Error fetching about data:', error);
      toast.error('Failed to load about page data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAboutContent = async () => {
    setSaving(true);
    try {
      const dataToSave = {
        ...i18nService.setTranslatedFields('companyStory', aboutData.companyStory),
        ...i18nService.setTranslatedFields('mission', aboutData.mission),
        ...i18nService.setTranslatedFields('vision', aboutData.vision)
      };
      await databaseService.update('website/aboutPage', dataToSave);
      toast.success('About content saved successfully');
    } catch (error) {
      toast.error('Failed to save about content');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return null;

    setUploadingPhoto(true);
    try {
      const photoUrl = await storageService.uploadFile(file, 'team');
      return photoUrl;
    } catch (error) {
      toast.error('Failed to upload photo');
      console.error(error);
      return null;
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleAddTeamMember = async () => {
    if (!newMember.name || !newMember.role) {
      toast.error('Name and role are required');
      return;
    }

    setSaving(true);
    try {
      let photoUrl = '';
      if (newMember.photo) {
        photoUrl = await handlePhotoUpload(newMember.photo);
        if (!photoUrl) {
          setSaving(false);
          return;
        }
      }

      const memberData = {
        name: newMember.name,
        role: newMember.role,
        bio: newMember.bio,
        photo: photoUrl,
        order: newMember.order || teamMembers.length
      };

      await databaseService.addTeamMember(memberData);
      toast.success('Team member added successfully');

      setNewMember({
        name: '',
        role: '',
        bio: '',
        photo: null,
        order: 0
      });

      fetchAboutData();
    } catch (error) {
      toast.error('Failed to add team member');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTeamMember = async (memberId) => {
    if (!editingMember) return;

    setSaving(true);
    try {
      let photoUrl = editingMember.photo;
      if (editingMember.newPhoto) {
        photoUrl = await handlePhotoUpload(editingMember.newPhoto);
        if (!photoUrl) {
          setSaving(false);
          return;
        }
      }

      const memberData = {
        name: editingMember.name,
        role: editingMember.role,
        bio: editingMember.bio,
        photo: photoUrl,
        order: editingMember.order
      };

      await databaseService.updateTeamMember(memberId, memberData);
      toast.success('Team member updated successfully');
      setEditingMember(null);
      fetchAboutData();
    } catch (error) {
      toast.error('Failed to update team member');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTeamMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to delete this team member?')) {
      return;
    }

    try {
      await databaseService.deleteTeamMember(memberId);
      toast.success('Team member deleted successfully');
      fetchAboutData();
    } catch (error) {
      toast.error('Failed to delete team member');
      console.error(error);
    }
  };


  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">About Page Manager</h1>
          <p className="text-gray-600">Manage your company story and team members</p>
        </div>

        {/* Company Story */}
        <Card>
          <h2 className="text-xl font-semibold mb-4">Company Story</h2>
          <LanguageTabs
            currentLang={currentLang}
            onLangChange={setCurrentLang}
          />
          <textarea
            value={aboutData.companyStory[currentLang]}
            onChange={(e) => setAboutData({
              ...aboutData,
              companyStory: { ...aboutData.companyStory, [currentLang]: e.target.value }
            })}
            className="w-full min-h-[200px] p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder={`Tell your company's story in ${currentLang.toUpperCase()}...`}
          />
          <p className="text-sm text-gray-500 mt-2">You can use HTML tags for formatting (e.g., &lt;p&gt;, &lt;strong&gt;, &lt;em&gt;). Switch between language tabs to edit content in different languages.</p>
        </Card>

        {/* Mission & Vision */}
        <Card>
          <h2 className="text-xl font-semibold mb-4">Mission Statement</h2>
          <LanguageTabs
            currentLang={currentLang}
            onLangChange={setCurrentLang}
          />
          <textarea
            value={aboutData.mission[currentLang]}
            onChange={(e) => setAboutData({
              ...aboutData,
              mission: { ...aboutData.mission, [currentLang]: e.target.value }
            })}
            className="w-full min-h-[150px] p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder={`Your company's mission in ${currentLang.toUpperCase()}...`}
          />
        </Card>

        <Card>
          <h2 className="text-xl font-semibold mb-4">Vision Statement</h2>
          <LanguageTabs
            currentLang={currentLang}
            onLangChange={setCurrentLang}
          />
          <textarea
            value={aboutData.vision[currentLang]}
            onChange={(e) => setAboutData({
              ...aboutData,
              vision: { ...aboutData.vision, [currentLang]: e.target.value }
            })}
            className="w-full min-h-[150px] p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder={`Your company's vision in ${currentLang.toUpperCase()}...`}
          />
        </Card>

        <div className="flex justify-end">
          <Button variant="primary" onClick={handleSaveAboutContent} loading={saving} disabled={saving}>
            {saving ? 'Saving...' : 'Save About Content'}
          </Button>
        </div>

        {/* Team Members */}
        <Card>
          <h2 className="text-xl font-semibold mb-4">Team Members</h2>

          {/* Add New Team Member */}
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h3 className="font-medium mb-3">Add New Team Member</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Name"
                value={newMember.name}
                onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                placeholder="Full Name"
              />
              <Input
                label="Role"
                value={newMember.role}
                onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
                placeholder="Position/Role"
              />
              <div className="md:col-span-2">
                <Input
                  label="Bio"
                  value={newMember.bio}
                  onChange={(e) => setNewMember({ ...newMember, bio: e.target.value })}
                  placeholder="Short bio..."
                  multiline
                  rows={3}
                />
              </div>
              <Input
                label="Photo"
                type="file"
                accept="image/*"
                onChange={(e) => setNewMember({ ...newMember, photo: e.target.files[0] })}
              />
              <Input
                label="Display Order"
                type="number"
                value={newMember.order}
                onChange={(e) => setNewMember({ ...newMember, order: parseInt(e.target.value) })}
                placeholder="0"
              />
            </div>
            <div className="mt-4">
              <Button
                variant="primary"
                onClick={handleAddTeamMember}
                loading={saving || uploadingPhoto}
                disabled={saving || uploadingPhoto}
              >
                {uploadingPhoto ? 'Uploading Photo...' : saving ? 'Adding...' : 'Add Team Member'}
              </Button>
            </div>
          </div>

          {/* Team Members List */}
          <div className="space-y-4">
            {teamMembers.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No team members yet. Add your first team member above.</p>
            ) : (
              teamMembers.map((member) => (
                <div key={member.id} className="border border-gray-200 rounded-lg p-4">
                  {editingMember?.id === member.id ? (
                    // Edit Mode
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          label="Name"
                          value={editingMember.name}
                          onChange={(e) => setEditingMember({ ...editingMember, name: e.target.value })}
                        />
                        <Input
                          label="Role"
                          value={editingMember.role}
                          onChange={(e) => setEditingMember({ ...editingMember, role: e.target.value })}
                        />
                        <div className="md:col-span-2">
                          <Input
                            label="Bio"
                            value={editingMember.bio}
                            onChange={(e) => setEditingMember({ ...editingMember, bio: e.target.value })}
                            multiline
                            rows={3}
                          />
                        </div>
                        <Input
                          label="New Photo (optional)"
                          type="file"
                          accept="image/*"
                          onChange={(e) => setEditingMember({ ...editingMember, newPhoto: e.target.files[0] })}
                        />
                        <Input
                          label="Display Order"
                          type="number"
                          value={editingMember.order}
                          onChange={(e) => setEditingMember({ ...editingMember, order: parseInt(e.target.value) })}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="primary"
                          onClick={() => handleUpdateTeamMember(member.id)}
                          loading={saving}
                          disabled={saving}
                        >
                          Save
                        </Button>
                        <Button variant="secondary" onClick={() => setEditingMember(null)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // View Mode
                    <div className="flex items-start gap-4">
                      {member.photo && (
                        <img
                          src={member.photo}
                          alt={member.name}
                          className="w-20 h-20 rounded-full object-cover"
                        />
                      )}
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg">{member.name}</h4>
                        <p className="text-primary-600">{member.role}</p>
                        {member.bio && <p className="text-gray-600 mt-2">{member.bio}</p>}
                        <p className="text-sm text-gray-400 mt-1">Order: {member.order}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setEditingMember({ ...member })}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteTeamMember(member.id)}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AboutManager;
